import {
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  delayWhen,
  distinctUntilChanged,
  EMPTY,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import type { Maybe } from '@plugins/shared/types';

import { tooltipPositions } from '../private/constants';
import { TooltipComponent } from '../private/tooltip.component';

export type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

@Directive({
  selector: '[pluginsTooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'mouseover()',
    '(mouseleave)': 'mouseout()',
    '(focusin)': 'focus()',
    '(focusout)': 'blur($event)',
  },
  exportAs: 'pluginsTooltip',
})
export class TooltipDirective implements OnInit, OnDestroy {
  @Input() set pluginsTooltip(val: Maybe<string>) {
    this.#text$.next(typeof val === 'string' ? val : '');
  }
  get pluginsTooltip() {
    return this.#text$.getValue();
  }
  @Input() set pluginsTooltipPosition(val: TooltipPosition) {
    this.#position$.next(val);
  }
  @Input() set pluginsTooltipDisabled(val: boolean) {
    this.#disabled$.next(val);
  }
  @Input() pluginsTooltipShowDelay = 0;

  #overlay = inject(Overlay);
  #overlayPositionBuilder = inject(OverlayPositionBuilder);
  #elemRef = inject<ElementRef<HTMLElement>>(ElementRef);

  #overlayRef: OverlayRef | null = null;

  #text$ = new BehaviorSubject<string>('');
  #position$ = new BehaviorSubject<TooltipPosition>('top');
  #createTooltip$ = combineLatest({
    text: this.#text$,
    position: this.#position$,
  }).pipe(
    tap(({ text, position }) => {
      this.#createTooltip(text, position);
    }),
  );

  #disabled$ = new BehaviorSubject<boolean>(false);
  #hovered$ = new BehaviorSubject<boolean>(false);
  #focused$ = new BehaviorSubject<boolean>(false);
  #show$ = combineLatest({
    text: this.#text$,
    disabled: this.#disabled$,
    hovered: this.#hovered$,
    focused: this.#focused$,
  }).pipe(
    map(
      ({ text, disabled, hovered, focused }) =>
        !disabled && (hovered || focused) && !!text.trim().length,
    ),
    distinctUntilChanged(),
    switchMap((show) =>
      of(show).pipe(
        delayWhen((show) => timer(show ? this.pluginsTooltipShowDelay : 0)),
      ),
    ),
  );

  #destroy$ = new Subject<void>();

  ngOnInit() {
    this.#show$
      .pipe(
        switchMap((show) => {
          if (show) {
            return this.#createTooltip$;
          }
          this.#overlayRef?.detach();
          return EMPTY;
        }),
        takeUntil(this.#destroy$),
      )
      .subscribe({
        complete: () => {
          this.#overlayRef?.detach();
        },
      });
  }

  mouseover() {
    this.#hovered$.next(true);
  }

  mouseout() {
    this.#hovered$.next(false);
  }

  focus() {
    this.#focused$.next(true);
  }

  blur(event?: FocusEvent) {
    if (
      !event ||
      (this.#elemRef.nativeElement !== event.relatedTarget &&
        !this.#elemRef.nativeElement.contains(event.relatedTarget as Node))
    ) {
      this.#focused$.next(false);
    }
  }

  #createTooltip(text: string, position: TooltipPosition) {
    this.#overlayRef?.detach();
    const positionStrategy = this.#buildPositionStrategy(position);
    this.#overlayRef = this.#overlay.create({
      positionStrategy,
    });
    const portal = new ComponentPortal(TooltipComponent);
    const ref = this.#overlayRef.attach(portal);
    ref.setInput('text', text);
  }

  #buildPositionStrategy(position: TooltipPosition) {
    return this.#overlayPositionBuilder
      .flexibleConnectedTo(this.#elemRef)
      .withPositions(tooltipPositions[position])
      .withPush();
  }

  ngOnDestroy() {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
